/**
 * Structured Logging System for Wixell Platform
 * Provides comprehensive logging with structured format, correlation IDs, and log levels
 */

class StructuredLogger {
  constructor() {
    this.logLevel = this.getLogLevel();
    this.correlationId = this.generateCorrelationId();
    this.sessionId = this.getSessionId();
    this.userId = null;
    this.tenantId = null;
    this.logBuffer = [];
    this.maxBufferSize = 1000;
    this.flushInterval = 5000; // 5 seconds
    
    this.init();
  }

  init() {
    // Set up periodic log flushing
    setInterval(() => {
      this.flushLogs();
    }, this.flushInterval);

    // Set up error handling for logger
    window.addEventListener('error', (event) => {
      this.error('Unhandled JavaScript error', {
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        stack: event.error?.stack
      });
    });

    window.addEventListener('unhandledrejection', (event) => {
      this.error('Unhandled Promise Rejection', {
        reason: event.reason?.message || event.reason,
        stack: event.reason?.stack
      });
    });

    // Flush logs on page unload
    window.addEventListener('beforeunload', () => {
      this.flushLogs();
    });
  }

  getLogLevel() {
    const envLogLevel = import.meta.env.VITE_LOG_LEVEL;
    const levelMap = {
      'debug': 0,
      'info': 1,
      'warn': 2,
      'error': 3,
      'fatal': 4
    };
    
    return levelMap[envLogLevel] || 1; // Default to info level
  }

  generateCorrelationId() {
    return `corr_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  getSessionId() {
    let sessionId = sessionStorage.getItem('logging_session_id');
    if (!sessionId) {
      sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      sessionStorage.setItem('logging_session_id', sessionId);
    }
    return sessionId;
  }

  createLogEntry(level, message, data = {}, context = {}) {
    const entry = {
      timestamp: new Date().toISOString(),
      level: level.toUpperCase(),
      message,
      data,
      context: {
        correlationId: this.correlationId,
        sessionId: this.sessionId,
        userId: this.userId || this.getCurrentUserId(),
        tenantId: this.tenantId || this.getCurrentTenantId(),
        userAgent: navigator.userAgent,
        url: window.location.href,
        referrer: document.referrer,
        ...context
      },
      metadata: {
        version: import.meta.env.VITE_APP_VERSION || '1.0.0',
        environment: import.meta.env.MODE || 'development',
        buildTime: import.meta.env.VITE_BUILD_TIME || null
      }
    };

    // Add performance context with safety checks
    if (window.performance && performance.timing) {
      try {
        entry.context.performance = {
          navigationStart: performance.timing.navigationStart,
          loadEventEnd: performance.timing.loadEventEnd,
          domContentLoaded: performance.timing.domContentLoadedEventEnd
        };
      } catch (error) {
        console.warn('[StructuredLogger] Performance timing access failed:', error);
        entry.context.performance = {
          error: 'Performance timing access failed',
          message: error.message
        };
      }
    } else {
      entry.context.performance = {
        error: 'Performance API not available'
      };
    }

    // Add memory context if available
    if (performance.memory) {
      entry.context.memory = {
        usedJSHeapSize: performance.memory.usedJSHeapSize,
        totalJSHeapSize: performance.memory.totalJSHeapSize,
        jsHeapSizeLimit: performance.memory.jsHeapSizeLimit
      };
    }

    return entry;
  }

  shouldLog(level) {
    const levelMap = {
      'debug': 0,
      'info': 1,
      'warn': 2,
      'error': 3,
      'fatal': 4
    };
    
    return levelMap[level] >= this.logLevel;
  }

  log(level, message, data = {}, context = {}) {
    if (!this.shouldLog(level)) {
      return;
    }

    const entry = this.createLogEntry(level, message, data, context);
    
    // Add to buffer
    this.logBuffer.push(entry);
    
    // Limit buffer size
    if (this.logBuffer.length > this.maxBufferSize) {
      this.logBuffer.shift();
    }

    // Console output for development
    if (import.meta.env.DEV) {
      this.consoleLog(entry);
    }

    // Immediately flush for error/fatal logs
    if (level === 'error' || level === 'fatal') {
      this.flushLogs();
    }
  }

  consoleLog(entry) {
    const consoleMethod = {
      'DEBUG': 'debug',
      'INFO': 'info',
      'WARN': 'warn',
      'ERROR': 'error',
      'FATAL': 'error'
    }[entry.level] || 'log';

    const message = `[${entry.timestamp}] ${entry.level} ${entry.message}`;
    
    if (entry.data && Object.keys(entry.data).length > 0) {
      console[consoleMethod](message, entry.data);
    } else {
      console[consoleMethod](message);
    }
  }

  debug(message, data = {}, context = {}) {
    this.log('debug', message, data, context);
  }

  info(message, data = {}, context = {}) {
    this.log('info', message, data, context);
  }

  warn(message, data = {}, context = {}) {
    this.log('warn', message, data, context);
  }

  error(message, data = {}, context = {}) {
    this.log('error', message, data, context);
  }

  fatal(message, data = {}, context = {}) {
    this.log('fatal', message, data, context);
  }

  // Specialized logging methods
  userAction(action, data = {}) {
    this.info(`User action: ${action}`, data, {
      category: 'user_action',
      action
    });
  }

  apiCall(method, url, data = {}) {
    this.debug(`API call: ${method} ${url}`, data, {
      category: 'api_call',
      method,
      url
    });
  }

  apiResponse(method, url, status, duration, data = {}) {
    const level = status >= 400 ? 'warn' : 'debug';
    this.log(level, `API response: ${method} ${url} - ${status} (${duration}ms)`, data, {
      category: 'api_response',
      method,
      url,
      status,
      duration
    });
  }

  performance(operation, duration, data = {}) {
    const level = duration > 1000 ? 'warn' : 'debug';
    this.log(level, `Performance: ${operation} took ${duration}ms`, data, {
      category: 'performance',
      operation,
      duration
    });
  }

  security(event, data = {}) {
    this.warn(`Security event: ${event}`, data, {
      category: 'security',
      event
    });
  }

  business(event, data = {}) {
    this.info(`Business event: ${event}`, data, {
      category: 'business',
      event
    });
  }

  // Context management
  setUserId(userId) {
    this.userId = userId;
    this.debug('User context set', { userId });
  }

  setTenantId(tenantId) {
    this.tenantId = tenantId;
    this.debug('Tenant context set', { tenantId });
  }

  clearContext() {
    this.userId = null;
    this.tenantId = null;
    this.debug('Context cleared');
  }

  // Log management
  async flushLogs() {
    if (this.logBuffer.length === 0) {
      return;
    }

    const logsToSend = [...this.logBuffer];
    this.logBuffer = [];

    try {
      await this.sendLogs(logsToSend);
    } catch (error) {
      console.error('[StructuredLogger] Failed to send logs:', error);
      
      // Re-add failed logs to buffer for retry
      this.logBuffer.unshift(...logsToSend);
      
      // Limit buffer size after re-adding
      if (this.logBuffer.length > this.maxBufferSize) {
        this.logBuffer = this.logBuffer.slice(0, this.maxBufferSize);
      }
    }
  }

  async sendLogs(logs) {
    if (import.meta.env.DEV && !import.meta.env.VITE_DEBUG_LOGGING) {
      return; // Don't send logs in development unless explicitly enabled
    }

    const response = await fetch('/api/logs', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Tenant-ID': this.tenantId || this.getCurrentTenantId()
      },
      body: JSON.stringify({
        logs,
        metadata: {
          clientVersion: import.meta.env.VITE_APP_VERSION || '1.0.0',
          timestamp: new Date().toISOString()
        }
      })
    });

    if (!response.ok) {
      throw new Error(`Failed to send logs: ${response.status} ${response.statusText}`);
    }
  }

  getCurrentUserId() {
    try {
      const authData = JSON.parse(localStorage.getItem('auth_user') || '{}');
      return authData.id || 'anonymous';
    } catch (e) {
      return 'anonymous';
    }
  }

  getCurrentTenantId() {
    try {
      const tenantData = JSON.parse(localStorage.getItem('tenant_context') || '{}');
      return tenantData.id || 'unknown';
    } catch (e) {
      return 'unknown';
    }
  }

  // Query methods
  getLogs(filter = {}) {
    let filteredLogs = [...this.logBuffer];

    if (filter.level) {
      filteredLogs = filteredLogs.filter(log => log.level === filter.level.toUpperCase());
    }

    if (filter.category) {
      filteredLogs = filteredLogs.filter(log => log.context.category === filter.category);
    }

    if (filter.since) {
      const since = new Date(filter.since);
      filteredLogs = filteredLogs.filter(log => new Date(log.timestamp) >= since);
    }

    if (filter.limit) {
      filteredLogs = filteredLogs.slice(-filter.limit);
    }

    return filteredLogs;
  }

  getLogStats() {
    const stats = {
      total: this.logBuffer.length,
      byLevel: {},
      byCategory: {},
      recent: this.logBuffer.slice(-10)
    };

    this.logBuffer.forEach(log => {
      stats.byLevel[log.level] = (stats.byLevel[log.level] || 0) + 1;
      
      if (log.context.category) {
        stats.byCategory[log.context.category] = (stats.byCategory[log.context.category] || 0) + 1;
      }
    });

    return stats;
  }

  // Export/Import for debugging
  exportLogs() {
    return {
      logs: this.logBuffer,
      correlationId: this.correlationId,
      sessionId: this.sessionId,
      userId: this.userId,
      tenantId: this.tenantId,
      timestamp: new Date().toISOString()
    };
  }

  clearLogs() {
    this.logBuffer = [];
    this.debug('Log buffer cleared');
  }
}

// Create and export singleton instance
const logger = new StructuredLogger();

export default logger;

// Export convenience methods
export const {
  debug,
  info,
  warn,
  error,
  fatal,
  userAction,
  apiCall,
  apiResponse,
  performance,
  security,
  business,
  setUserId,
  setTenantId,
  clearContext,
  getLogs,
  getLogStats,
  exportLogs,
  clearLogs
} = logger;

// React hook for using logger in components
export const useLogger = () => {
  return {
    debug,
    info,
    warn,
    error,
    fatal,
    userAction,
    apiCall,
    apiResponse,
    performance,
    security,
    business,
    setUserId,
    setTenantId,
    clearContext
  };
};